import {
    BooleanField,
    BooleanInput,
    DateInput,
    Filter,
    FunctionField,
    TextField,
    useLocaleState,
    usePermissions,
} from "react-admin";
import { Chip } from "@mui/material";
import { DefaultPagination } from "shared/src/components/DefaultPagination";
import { ToolbarWithHistory } from "shared/src/components/ToolbarWithHistory";
import { DatagridWithHistory } from "shared/src/components/DatagridWithHistory";
import { AccessRightsError } from "shared/src/components/AccessRightsError";
import { RaListView } from "shared/src/components/views/RaListView";
import { RaDateField } from "shared/src/components/fields/Ra/RaDateField";

const rowSx = (record) =>
    record?.hasRelevantAlerts
        ? {
              backgroundColor: "rgba(211, 47, 47, 0.08)",
              "&:hover": {
                  backgroundColor: "rgba(211, 47, 47, 0.14)",
              },
          }
        : {};

const getSuggestedOrderQuantity = (record) =>
    Math.max(record.currentSuggestedOrderQuantity || 0, 0);

const ProductStockAlertFilters = (props) => (
    <Filter {...props}>
        <BooleanInput alwaysOn source="isRelevant" label="equip.onlyOpenStockAlerts" />
        <DateInput alwaysOn source="fromAlertDate" label="equip.fromAlertDate" />
        <DateInput alwaysOn source="toAlertDate" label="equip.toAlertDate" />
    </Filter>
);

export const ProductStockAlertList = (props) => {
    const { isLoading, permissions } = usePermissions();
    const [locale] = useLocaleState();

    if (isLoading) {
        return <></>;
    } else if (!permissions.vote({ resource: "products", attribute: "read" })) {
        return <AccessRightsError />;
    }

    return (
        <RaListView
            {...props}
            resource="productStockAlerts"
            actions={<ToolbarWithHistory export={true} />}
            pagination={<DefaultPagination />}
            perPage={50}
            sort={{ field: "highestMissingQuantity", order: "DESC" }}
            filterDefaultValues={{ isRelevant: true }}
            filters={<ProductStockAlertFilters />}
        >
            <DatagridWithHistory
                bulkActionButtons={false}
                rowClick={(id, resource, record) => `/products/${record.product.id}/show`}
                rowSx={rowSx}
            >
                <TextField label="equip.labelNumber" source="product.labelNumber" />
                <TextField label="equip.name" source={`product.name.${locale}`} />
                <TextField label="equip.category" source="product.category.path" />
                <TextField label="equip.manufacturer" source="product.manufacturer.name" />
                <TextField label="equip.supplier" source="product.supplier.name" />
                <TextField label="equip.actualQuantity" source="product.actualQuantity" />
                <TextField label="equip.minimumQuantity" source="product.minimumQuantity" />
                <TextField label="equip.daysBelowThreshold" source="daysBelowThreshold" />
                <TextField label="equip.relevantAlertDays" source="relevantAlertDays" />
                <TextField label="equip.lowestActualQuantity" source="lowestActualQuantity" />
                <TextField label="equip.highestMissingQuantity" source="highestMissingQuantity" />
                <TextField label="equip.currentMissingQuantity" source="currentMissingQuantity" />
                <FunctionField
                    label="equip.movementMarkup"
                    render={(record) =>
                        `${record.movementMarkupQuantity || 0} (${record.movementMarkupPercentage || 0}%)`
                    }
                    sortable={false}
                />
                <FunctionField
                    label="equip.suggestedOrderQuantity"
                    render={(record) => (
                        <Chip
                            color={record.hasRelevantAlerts ? "error" : "default"}
                            size="small"
                            label={getSuggestedOrderQuantity(record)}
                        />
                    )}
                    sortable={false}
                />
                <RaDateField label="equip.firstAlertDate" source="firstAlertDate" />
                <RaDateField label="equip.lastAlertDate" source="lastAlertDate" />
                <BooleanField label="equip.hasRelevantAlerts" source="hasRelevantAlerts" />
            </DatagridWithHistory>
        </RaListView>
    );
};
